import NumberFlow from '@number-flow/react';
import { lighten } from 'polished';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { Box } from 'reflexbox';
import { useTheme } from 'styled-components';

import { tokens } from '../../theme/tokens';
import { Delta } from '../Delta';

const ANIMATION_MS = 900;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

const useAnimatedValue = (target: number, initial: number) => {
	const [current, setCurrent] = useState(initial);
	const frame = useRef<number>();
	const fromRef = useRef(initial);

	useEffect(() => {
		const from = fromRef.current;
		const start = performance.now();
		const step = (now: number) => {
			const progress = Math.min((now - start) / ANIMATION_MS, 1);
			const next = from + (target - from) * easeOutCubic(progress);

			fromRef.current = next;
			setCurrent(next);

			if (progress < 1) {
				frame.current = requestAnimationFrame(step);
			}
		};
		frame.current = requestAnimationFrame(step);
		return () => {
			if (frame.current) cancelAnimationFrame(frame.current);
		};
	}, [target]);

	return current;
};

type Props = {
	value: number;
	min?: number;
	max?: number;
	label?: string;
	ticks?: number[];
	markers?: {
		value: number;
		label?: string;
	}[];
	delta?: number;
	color?: string;
	size?: number;
	compact?: boolean;
	format?: ComponentProps<typeof NumberFlow>['format'];
};

const STROKE_WIDTH = 7;
const TICK_OFFSET = 22;
const GAP_PX = 5;

const strikeColor = (score: number, colors: ReturnType<typeof useTheme>['colors']) => {
	if (score >= 8.5) return colors.primary;

	if (score >= 7.5) return colors.green;

	if (score >= 6.5) return colors.yellow;

	if (score >= 5) return colors.warning;

	return colors.danger;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const GaugeMeter = (props: Props) => {
	const theme = useTheme();
	const {
		value,
		min = 0,
		max = 10,
		label,
		ticks = [0, 5, 6.5, 7.5, 8.5, 10],
		markers = [],
		delta,
		size = 220,
		compact = false,
		format = { minimumFractionDigits: 1, maximumFractionDigits: 1 },
	} = props;

	const color = strikeColor(value, theme.colors);
	const knobColor = color === theme.colors.green ? lighten(0.03, theme.colors.green) : color;

	const strokeWidth = compact ? 4 : STROKE_WIDTH;
	const tickOffset = compact ? 0 : TICK_OFFSET;

	const animatedValue = useAnimatedValue(value, min);

	const [displayValue, setDisplayValue] = useState(min);
	useEffect(() => {
		setDisplayValue(value);
	}, [value]);

	const radius = (size - strokeWidth * 2 - tickOffset * 2) / 2;
	const cx = size / 2;
	const cy = strokeWidth + tickOffset + radius;
	const height = cy + strokeWidth;

	const fractionFor = (v: number) => clamp((v - min) / (max - min), 0, 1);

	const pointFor = (v: number, atRadius = radius) => {
		const angle = Math.PI - fractionFor(v) * Math.PI;
		return {
			x: cx + atRadius * Math.cos(angle),
			y: cy - atRadius * Math.sin(angle),
		};
	};

	const gapAngle = compact ? 0 : GAP_PX / radius;

	const angleFor = (v: number) => Math.PI - fractionFor(v) * Math.PI;
	const pointAtAngle = (angle: number) => ({
		x: cx + radius * Math.cos(angle),
		y: cy - radius * Math.sin(angle),
	});

	const segmentPath = (from: number, to: number, gapStart: boolean, gapEnd: boolean) => {
		const a0 = angleFor(from) - (gapStart ? gapAngle : 0);
		const a1 = angleFor(to) + (gapEnd ? gapAngle : 0);
		if (a0 <= a1) {
			return null;
		}
		const p0 = pointAtAngle(a0);
		const p1 = pointAtAngle(a1);
		return `M ${p0.x} ${p0.y} A ${radius} ${radius} 0 0 1 ${p1.x} ${p1.y}`;
	};

	const dividers = ticks.filter((tick) => tick > min && tick < max).sort((a, b) => a - b);
	const boundaries = [min, ...dividers, max];
	const segments = boundaries.slice(0, -1).map((from, index) => ({
		from,
		to: boundaries[index + 1],
		first: index === 0,
		last: index === boundaries.length - 2,
	}));

	const knob = pointFor(animatedValue);

	return (
		<Box
			style={{
				display: 'inline-flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 0,
				lineHeight: 1.5,
				width: 'fit-content',
			}}
		>
			<Box width={size} height={height} style={{ position: 'relative' }}>
				<svg
					width={size}
					height={height}
					aria-hidden="true"
					focusable="false"
					style={{ display: 'block', overflow: 'visible' }}
				>
					{segments.map((segment) => {
						const track = segmentPath(segment.from, segment.to, !segment.first, !segment.last);
						if (!track) {
							return null;
						}

						const greenTo = Math.min(segment.to, animatedValue);
						const valuePath =
							animatedValue > segment.from
								? segmentPath(
										segment.from,
										greenTo,
										!segment.first,
										greenTo === segment.to && !segment.last,
									)
								: null;

						return (
							<g key={segment.from}>
								<path
									d={track}
									strokeWidth={strokeWidth}
									style={{
										fill: 'none',
										stroke: theme.colors.lightBlueLight,
										strokeLinecap: 'round',
									}}
								/>
								{valuePath && (
									<path
										d={valuePath}
										strokeWidth={strokeWidth}
										style={{
											fill: 'none',
											stroke: color,
											strokeLinecap: 'round',
										}}
									/>
								)}
							</g>
						);
					})}

					{!compact && (
						<circle cx={knob.x} cy={knob.y} r={strokeWidth / 2 + 4} style={{ fill: knobColor }} />
					)}

					{!compact &&
						ticks.map((tick) => {
							const point = pointFor(tick, radius + tickOffset);
							const cos = Math.cos(Math.PI - fractionFor(tick) * Math.PI);
							const anchor = cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle';

							return (
								<text
									key={tick}
									x={point.x}
									y={point.y + 6}
									textAnchor={anchor}
									style={{
										fill: tokens.text.secondary,
										fontSize: 13,
										fontWeight: 400,
									}}
								>
									{tick}
								</text>
							);
						})}

					{markers.map((marker) => {
						const point = pointFor(marker.value);

						return (
							<g key={`${marker.value}-${marker.label ?? 'P'}`}>
								<circle
									cx={point.x}
									cy={point.y}
									r={11}
									style={{
										fill: tokens.text.secondary,
										stroke: theme.colors.white,
										strokeWidth: 21,
									}}
								/>
								<text
									x={point.x}
									y={point.y + 4}
									textAnchor="middle"
									style={{
										fill: theme.colors.white,
										fontSize: 11,
										fontWeight: 700,
									}}
								>
									{marker.label ?? 'P'}
								</text>
							</g>
						);
					})}
				</svg>

				<Box
					style={{
						position: 'absolute',
						bottom: compact ? strokeWidth : 0,
						left: '50%',
						transform: 'translateX(-50%)',
					}}
				>
					<Box
						style={{
							color: tokens.text.primary,
							fontSize: compact ? 18 : 70,
							fontWeight: 700,
							lineHeight: 1,
							letterSpacing: compact ? '-1px' : '-2px',
							textBoxTrim: 'trim-both',
							textBoxEdge: 'cap alphabetic',
						}}
					>
						<NumberFlow value={displayValue} format={format} locales="en-US" />
					</Box>
					{label && !compact && (
						<Box
							style={{
								marginTop: 12,
								color: tokens.text.primary,
								fontSize: compact ? 12 : 16,
								fontWeight: 'normal',
								textAlign: 'center',
								whiteSpace: 'nowrap',
							}}
						>
							{label}
						</Box>
					)}
				</Box>
			</Box>
			{label && compact && (
				<Box
					style={{
						marginTop: 8,
						color: tokens.text.primary,
						fontSize: compact ? 12 : 16,
						fontWeight: 'normal',
						textAlign: 'center',
						whiteSpace: 'nowrap',
					}}
				>
					{label}
				</Box>
			)}
			{delta !== undefined && !compact && (
				<Delta
					unit="numeric"
					currentValue={value}
					previousValue={value - delta}
					style={{ marginTop: 16 }}
					animate
				/>
			)}
		</Box>
	);
};

export default GaugeMeter;
